// services/conversation/conversationApi.ts
import baseApi from "@/lib/baseApi";
import uploadApi from "@/lib/uploadApi";
import { getDeviceId } from "../../utils/common/deviceHelpers";
import { getDeviceIdAsync } from "../../utils/keyManager";
import { Dialogue, Message, UserDialogueMarker, SeenUser, DialogueUnreadCount, DialogueUnreadCountsResponse } from '../../types/conversation/conversationTypes';


// ---------------------- Dialogue APIs ----------------------

// Create New Dialogue Api
export const createNewDialogueApi = async (
  recipient_id: number,
  check_only: boolean = false
): Promise<{
  dialogue?: Dialogue;
  message: string;
}> => {
  try {
    const response = await baseApi.post("/conversations/dialogues/create-dialogue/", {
      recipient_id,
      check_only,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 204) {
      throw {
        code: 204,
        message: "Dialogue does not exist",
      };
    }
    throw error;
  }
};

// Create a new group chat
export const createGroupApi = async (data: { name: string; image?: File }): Promise<Dialogue> => {
  const formData = new FormData();

  formData.append("group_name", data.name);
  if (data.image) {
    formData.append("group_image", data.image);
  }

  const response = await baseApi.post('/conversations/dialogues/create-group/', formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
  return response.data;
};

// Update Group Image API
export const updateGroupImageApi = async ({
  dialogueSlug,
  imageFile,
}: {
  dialogueSlug: string;
  imageFile: File;
}): Promise<{ detail: string }> => {
  const formData = new FormData();
  formData.append("group_image", imageFile);

  const response = await uploadApi.post(
    `/conversations/dialogues/${dialogueSlug}/update-group-image/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

// Update Group Info Api
export const updateGroupInfoApi = async ({
  dialogueSlug,
  data,
}: {
  dialogueSlug: string;
  data: Partial<{ group_name: string; info: string; bio: string }>;
}): Promise<{ detail: string; updated: Record<string, any>; unsupported: string[] }> => {
  const res = await baseApi.post(
    `/conversations/dialogues/${dialogueSlug}/update-group-info/`,
    data
  );
  return res.data;
};

// Smart delete a dialogue (based on user's role)
export const smartDeleteDialogueApi = async (
  dialogueSlug: string
): Promise<{ message: string }> => {
  const response = await baseApi.post(
    `/conversations/dialogues/${dialogueSlug}/smart-delete/`
  );
  return response.data;
};

export const fetchDialogueBySlugApi = async (
  dialogueSlug: string
): Promise<Dialogue> => {
  const deviceId = await getDeviceIdAsync();

  const response = await baseApi.get(
    `/conversations/dialogues/${dialogueSlug}/`,
    {
      headers: {
        "X-Device-ID": deviceId,
      },
      params: {
        device_id: deviceId,
      },
    }
  );

  return response.data;
};

// Add a participant to a group
export const addParticipantApi = async (
  dialogueSlug: string,
  participantId: string
): Promise<void> => {
  await baseApi.post(
    `/conversations/dialogues/${dialogueSlug}/add-participant/`,
    { participant_id: participantId }
  );
};

// Remove a participant from a group
export const removeParticipantApi = async (
  dialogueSlug: string,
  participantId: string
): Promise<void> => {
  await baseApi.post(
    `/conversations/dialogues/${dialogueSlug}/remove-participant/`,
    { participant_id: participantId }
  );
};

// ✅ Get participants of a group
export const fetchGroupParticipantsApi = async (dialogueSlug: string) => {
  const response = await baseApi.get(`/conversations/dialogues/${dialogueSlug}/participants/`);
  return response.data;
};

// Promote To Elder Api
export const promoteToElderApi = async (
  dialogueSlug: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/dialogues/${dialogueSlug}/promote-to-elder/`, {
    user_id: userId,
  });
  return response.data;
};

// Demote To Participant Api
export const demoteToParticipantApi = async (
  dialogueSlug: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/dialogues/${dialogueSlug}/demote-to-participant/`, {
    user_id: userId,
  });
  return response.data;
};

// Resign Elder Role Api
export const resignElderRoleApi = async (
  dialogueSlug: string
): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/dialogues/${dialogueSlug}/resign-elder-role/`);
  return response.data;
};

// Leave Group Api
export const leaveGroupApi = async (
  dialogueSlug: string
): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/dialogues/${dialogueSlug}/leave-group/`);
  return response.data;
};

// Transfer Founder Api
export const transferFounderApi = async (
  dialogueSlug: string,
  userId: number
): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/dialogues/${dialogueSlug}/transfer-founder/`, {
    user_id: userId,
  });
  return response.data;
};


// Get Public Keys of Chat Partner (For Decryption) -------------------------------------------
type Key = { device_id: string; public_key: string };
type DialogueKeysResponse = Key[] | { verified: Key[]; unverified: Key[] };

export async function getDialogueKeysApi(
  dialogueSlug: string,
  opts?: { full?: boolean }
): Promise<DialogueKeysResponse> {
  const suffix = opts?.full ? "?full=1" : "";
  const res = await baseApi.get(`/conversations/dialogues/${dialogueSlug}/keys/${suffix}`);
  return res.data as DialogueKeysResponse;
}

export function normalizeDialogueKeys(resp: DialogueKeysResponse): { verified: Key[]; unverified: Key[] } {
  if (Array.isArray(resp)) {
    return { verified: resp, unverified: [] };
  }
  return { verified: resp.verified || [], unverified: resp.unverified || [] };
}

// Enter chat with PIN for security access ------------------------------------------------
export const enterChatApi = async (): Promise<{ dialogues: Dialogue[] }> => {
  const deviceId = await getDeviceIdAsync();
  const response = await baseApi.post(
    "/conversations/dialogues/enter-chat/",
    null,
    {
      headers: { "X-Device-ID": deviceId },
      params: { device_id: deviceId },
    }
  );
  return response.data;
};

// Fetch last seen for a specific dialogue participant --------------------------------------
export const fetchLastSeenApi = async (dialogueSlug: string): Promise<{
  user_id: number;
  is_online: boolean;
  last_seen: string | null;
  last_seen_display: string;
}> => {
  const response = await baseApi.get(`/conversations/dialogues/${dialogueSlug}/last-seen/`);
  return response.data;
};

// Fetch unread counts for all dialogues
export const fetchUnreadCountsApi = async (): Promise<DialogueUnreadCount[]> => {
  const response = await baseApi.get<DialogueUnreadCountsResponse>(
    "/conversations/dialogues/unread-counts/"
  );

  const data = response.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data.results)) {
    return data.results;
  }

  if (Array.isArray(data.unread_counts)) {
    return data.unread_counts;
  }

  return [];
};


// ---------------------------------- Message APIs ----------------------------------

// Fetch messages from a dialogue
export const fetchMessagesApi = async (
  dialogueSlug: string,
  offset: number,
  limit: number
): Promise<{ is_encrypted: boolean; messages: Message[]; has_more: boolean }> => {
  const deviceId = await getDeviceIdAsync();
  const response = await baseApi.get("/conversations/messages/", {
    headers: { "X-Device-ID": deviceId },
    params: {
      dialogue_slug: dialogueSlug,
      offset,
      limit,
      device_id: deviceId,
    },
  });
  return {
    is_encrypted: response.data.is_encrypted,
    messages: response.data.messages,
    has_more: response.data.has_more,
  };
};


// Fetch Message By ID API -----------------------------------------
export const fetchMessageByIdApi = async (messageId: number): Promise<Message> => {
  const response = await baseApi.get(`/conversations/messages/${messageId}/get-message/`);
  return response.data;
};

// Search messages in a dialogue ----------------------------------
export const searchMessagesApi = async (
  dialogueSlug: string,
  query: string
): Promise<Message[]> => {
  const metaRes = await fetchMessagesApi(dialogueSlug, 0, 1);
  const isEncrypted = metaRes.is_encrypted;
  if (!isEncrypted) {
    const res = await baseApi.get(
      `/conversations/messages/${dialogueSlug}/search-messages/?q=${encodeURIComponent(query)}`
    );
    return res.data;
  }
  const allMessagesResponse = await fetchMessagesApi(dialogueSlug, 0, 1000);
  const matchedMessages = allMessagesResponse.messages.filter((message) =>
    message.decrypted_content?.toLowerCase().includes(query.toLowerCase())
  );

  return matchedMessages;
};


// Send a message --------------------------------------------------------------
export const sendMessageApi = async (data: {
  dialogue_slug: string;
  is_encrypted?: boolean;
  encrypted_contents?: { device_id: string; encrypted_content: string }[];
  content?: string;
}): Promise<{ dialogue_slug: string; message_id: number }> => {
  const { dialogue_slug, is_encrypted = false, encrypted_contents = [], content } = data;
  const payload: any = { dialogue_slug, is_encrypted };

  if (is_encrypted) {
    payload.encrypted_contents = encrypted_contents;
  } else {
    if (!content) throw new Error("Content is required for non-encrypted messages.");
    payload.content = content;
  }

  const response = await baseApi.post("/conversations/messages/send-message/", payload);
  return response.data;
};

// Upload File API For Conversation ------------------------------
export const uploadFileApi = async (
  data: {
    dialogue_slug: string;
    file: File;
    file_type: "image" | "video" | "audio" | "file";
    is_encrypted_file: boolean;
    aes_key_encrypted?: string;
    encrypted_keys_per_device?: { [device_id: string]: string };
  },
  onUploadProgress?: (progress: number) => void,
  controller?: AbortController
): Promise<{
  dialogue_slug: string;
  message_id: number;
  file_url: string;
  file_type: string;
  is_encrypted_file: boolean;
}> => {
  const {
    dialogue_slug,
    file,
    file_type,
    is_encrypted_file,
    aes_key_encrypted,
    encrypted_keys_per_device,
  } = data;

  const deviceId = await getDeviceIdAsync();

  const formData = new FormData();
  formData.append("dialogue_slug", dialogue_slug);
  formData.append("file_type", file_type);
  formData.append("file", file);
  formData.append("encrypted_for_device", deviceId);
  formData.append("is_encrypted_file", is_encrypted_file ? "true" : "false");

  if (is_encrypted_file) {
    if (aes_key_encrypted) {
      formData.append("aes_key_encrypted", aes_key_encrypted);
    }
    if (encrypted_keys_per_device) {
      formData.append("encrypted_keys_per_device", JSON.stringify(encrypted_keys_per_device));
    }
  }

  const response = await uploadApi.post(
    "/conversations/messages/upload-file/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Device-ID": deviceId,
      },
      signal: controller?.signal,
      onUploadProgress: (e) => {
        const loaded = e.loaded;
        const total = e.total ?? (loaded + 1);
        const percent = Math.round((loaded * 100) / total);
        onUploadProgress?.(percent);
      },
    }
  );

  return {
    ...response.data,
    file_type,
    is_encrypted_file,
  };
};


// Access encrypted media in a message ----------------------------
export const accessMediaApi = async (
  messageId: number,
  mediaType: "image" | "video" | "audio" | "file"
): Promise<
  | { encrypted_file: string; encrypted_aes_key: string }
  | { download_url: string; encrypted_aes_key: string }
> => {
  const deviceId = await getDeviceIdAsync();

  const response = await baseApi.get(`/conversations/messages/${messageId}/access-media/`, {
    headers: { "X-Device-ID": deviceId },
    params: { media_type: mediaType, device_id: deviceId },
    responseType: "json",
  });

  return response.data;
};


// Edit a message in a private or group chat ---------------------
export const editMessageApi = async (data: {
  message_id: number;
  is_encrypted?: boolean;
  encrypted_contents?: { device_id: string; encrypted_content: string }[];
  content?: string;
}): Promise<{ message: string }> => {
  const { message_id, is_encrypted = false, encrypted_contents, content } = data;
  const response = await baseApi.post(`/conversations/messages/${message_id}/edit-message/`, {
    is_encrypted,
    encrypted_contents,
    content,
  });
  return response.data;
};

// Soft delete -----------------------------------------------------------------
export const softDeleteMessageApi = async (message_id: number): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/messages/${message_id}/soft-delete/`);
  return response.data;
};

// Hard delete -----------------------------------------------------------------
export const hardDeleteMessageApi = async (message_id: number): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/messages/${message_id}/hard-delete/`);
  return response.data;
};

// Mark message as delivered -------------------------------
export const markMessageAsDeliveredApi = async (data: {
  dialogue_slug: string;
  message_id: number;
}): Promise<{ message: string }> => {
  const response = await baseApi.post(`/conversations/messages/mark-as-delivered/`, {
    dialogue_slug: data.dialogue_slug,
    message_id: data.message_id,
  });
  return response.data;
};


// Mark messages as read ----------------------------------------
export const markMessagesAsReadApi = async (
  dialogue_slug: string,
  message_ids: number[]
): Promise<{ message: string }> => {
  try {
    const response = await baseApi.post(`/conversations/messages/mark-as-read/`, {
      dialogue_slug,
      message_ids,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};


// Get Seen-By -----------------------------------------------------------------
export const getSeenByUsersApi = async (messageId: number): Promise<SeenUser[]> => {
  const response = await baseApi.get(`/conversations/messages/${messageId}/seen-by/`);
  return response.data;
};

// ---------------------- User Dialogue Marker APIs ----------------------

// Mark a dialogue as sensitive or update its delete policy
export const markDialogueApi = async (data: {
  dialogue_slug: string;
  is_sensitive?: boolean;
  delete_policy?: string;
}): Promise<UserDialogueMarker> => {
  const response = await baseApi.post('/conversations/markers/mark-dialogue/', data);
  return response.data;
};

// Unmark a dialogue as sensitive
export const unmarkDialogueApi = async (markerId: number): Promise<void> => {
  await baseApi.post(`/conversations/markers/${markerId}/unmark-dialogue/`);
};

